import { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import api from "@/lib/api";
import { compressImage } from "@/lib/image";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import crown from "@/assets/crown.png";

const labelCls = "text-xs uppercase tracking-widest text-[#A0A0A0]";
const inputCls =
  "bg-[#15151A] border-white/10 text-white placeholder:text-[#5a5a66] focus:border-[#D4AF37] focus-visible:ring-[#D4AF37]/50";

const PhotoSection = ({ form, onSetPhoto, fileRef, onFileChange }) => (
  <div className="flex flex-col items-center gap-4">
    <Avatar className="w-24 h-24 border-2 border-[#D4AF37]/50" data-testid="profile-photo-preview">
      <AvatarImage src={form.photo} alt="Profile" />
      <AvatarFallback className="bg-[#15151A] text-[#D4AF37] font-serif text-2xl">
        {form.name?.[0] || "?"}
      </AvatarFallback>
    </Avatar>
    <div className="flex w-full gap-3">
      <Input
        data-testid="profile-photo-url-input"
        placeholder="Photo URL"
        value={form.photo.startsWith("data:") ? "" : form.photo}
        onChange={(e) => onSetPhoto(e.target.value)}
        className={inputCls}
      />
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFileChange} />
      <Button
        type="button"
        variant="outline"
        data-testid="profile-photo-upload-button"
        onClick={() => fileRef.current?.click()}
        className="border-[#D4AF37]/60 text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:text-[#F1D570] bg-transparent shrink-0 transition-colors"
      >
        <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} />
        Upload
      </Button>
    </div>
  </div>
);

const GenderSelect = ({ value, onChange }) => (
  <div className="space-y-2">
    <Label className={labelCls}>Gender</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid="profile-gender-select" className={inputCls}>
        <SelectValue placeholder="Select" />
      </SelectTrigger>
      <SelectContent className="bg-[#15151A] border-[#D4AF37]/25 text-white">
        {["Male", "Female", "Other"].map((g) => (
          <SelectItem key={g} value={g} data-testid={`profile-gender-${g.toLowerCase()}`}>
            {g}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

const WhatsAppField = ({ value, onChange }) => (
  <div className="space-y-2">
    <Label className={labelCls}>WhatsApp Number</Label>
    <div className="flex">
      <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-white/10 bg-[#1A1A24] text-[#D4AF37] text-sm font-medium">
        +91
      </span>
      <Input
        data-testid="profile-whatsapp-input"
        type="tel"
        maxLength={10}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))}
        placeholder="10-digit number"
        className={`${inputCls} rounded-l-none`}
      />
    </div>
  </div>
);

export default function CompleteProfile() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    name: user?.name || "",
    photo: user?.photo || "",
    age: "",
    gender: "",
    neighborhood: "",
    bio: "",
    whatsapp: "",
    terms_accepted: false,
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image must be under 2MB");
      return;
    }
    try {
      const dataUrl = await compressImage(file);
      set("photo", dataUrl);
      toast.success("Photo added");
    } catch {
      toast.error("Could not read that image");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const age = parseInt(form.age, 10);
    if (!form.name.trim()) return toast.error("Name is required");
    if (!age || age < 18) return toast.error("You must be 18 or older");
    if (!form.gender) return toast.error("Please select your gender");
    if (!form.neighborhood.trim()) return toast.error("Neighborhood is required");
    if (!form.bio.trim()) return toast.error("A short bio is required");
    if (!/^[6-9]\d{9}$/.test(form.whatsapp.replace(/\D/g, "").replace(/^91(?=\d{10}$)/, "")))
      return toast.error("Enter a valid 10-digit Indian WhatsApp number");
    if (!form.terms_accepted) return toast.error("Please accept the Terms of Service and Privacy Policy");

    setSaving(true);
    try {
      const { data } = await api.put("/profile", { ...form, age });
      setUser(data);
      toast.success("Welcome to THE CIRCLE");
      navigate("/circle");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0F] text-white">
      <div className="max-w-lg mx-auto px-6 py-12" data-testid="complete-profile-page">
        <div className="flex flex-col items-center text-center mb-10 fade-up">
          <img src={crown} alt="" className="w-14 h-14 mb-4" />
          <h1 className="font-serif text-3xl font-bold tracking-tight">Complete Your Profile</h1>
          <p className="text-sm text-[#A0A0A0] mt-2">
            The Circle is curated. Tell us who joins the table.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-6 fade-up" style={{ animationDelay: "100ms" }}>
          <PhotoSection
            form={form}
            onSetPhoto={(v) => set("photo", v)}
            fileRef={fileRef}
            onFileChange={handleFile}
          />

          <div className="space-y-2">
            <Label className={labelCls}>Name</Label>
            <Input
              data-testid="profile-name-input"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="Your full name"
              className={inputCls}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className={labelCls}>Age</Label>
              <Input
                data-testid="profile-age-input"
                type="number"
                min="18"
                value={form.age}
                onChange={(e) => set("age", e.target.value)}
                placeholder="18+"
                className={inputCls}
              />
            </div>
            <GenderSelect value={form.gender} onChange={(v) => set("gender", v)} />
          </div>

          <div className="space-y-2">
            <Label className={labelCls}>Neighborhood</Label>
            <Input
              data-testid="profile-neighborhood-input"
              value={form.neighborhood}
              onChange={(e) => set("neighborhood", e.target.value)}
              placeholder="e.g., Salt Lake, Park Street"
              className={inputCls}
            />
          </div>

          <div className="space-y-2">
            <Label className={labelCls}>Bio</Label>
            <Textarea
              data-testid="profile-bio-input"
              value={form.bio}
              onChange={(e) => set("bio", e.target.value)}
              placeholder="A few words about you..."
              maxLength={300}
              rows={3}
              className={inputCls}
            />
          </div>

          <WhatsAppField value={form.whatsapp} onChange={(v) => set("whatsapp", v)} />

          <label className="flex items-start gap-2.5 text-sm text-[#A0A0A0] cursor-pointer">
            <Checkbox
              data-testid="terms-checkbox"
              checked={form.terms_accepted}
              onCheckedChange={(v) => set("terms_accepted", !!v)}
              className="mt-0.5 border-white/20 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
            />
            <span>
              I agree to THE CIRCLE's{" "}
              <Link to="/terms" target="_blank" className="text-[#D4AF37] underline underline-offset-2">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="/privacy" target="_blank" className="text-[#D4AF37] underline underline-offset-2">
                Privacy Policy
              </Link>
              , and understand I attend any meetup at my own risk.
            </span>
          </label>

          <Button
            type="submit"
            disabled={saving}
            data-testid="profile-submit-button"
            className="w-full h-12 bg-[#D4AF37] text-[#0B0B0F] hover:bg-[#F1D570] font-semibold rounded-md text-base transition-colors"
          >
            {saving ? "Saving..." : "Enter the Circle"}
          </Button>
        </form>
      </div>
    </div>
  );
}
